import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Activity, ArrowLeft, CheckCircle2, Loader2, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const invite = params.get("invite") === "1";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7F9] px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#0047AB] flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900">CertIV Sims</span>
        </div>

        <Card className="border-slate-200 p-7" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center">
              <h1 className="text-xl font-bold text-slate-900">Invalid link</h1>
              <p className="text-sm text-slate-600 mt-2">This reset link is missing its token.</p>
              <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0047AB] mt-5">
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{invite ? "You're all set" : "Password reset"}</h1>
              <p className="text-sm text-slate-600 mt-2">
                {invite
                  ? "Your password is set. Sign in to start training with your team."
                  : "Your password has been updated. You can now sign in with your new password."}
              </p>
              <Button onClick={() => navigate("/login")} data-testid="reset-goto-login"
                className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white">
                Go to sign in
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#0047AB] mb-1">
                <KeyRound className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-widest font-semibold">
                  {invite ? "Welcome to CertIV Sims" : "Choose a new password"}
                </span>
              </div>
              <h1 className="text-xl font-bold text-slate-900">{invite ? "Set your password" : "Reset your password"}</h1>
              {invite && (
                <p className="text-sm text-slate-600 mt-2">
                  You've been added to a training team. Choose a password to activate your account.
                </p>
              )}
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="rp-pw" className="text-xs">{invite ? "Password" : "New password"}</Label>
                  <Input id="rp-pw" type="password" required minLength={6} value={pw}
                    onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rp-confirm" className="text-xs">{invite ? "Confirm password" : "Confirm new password"}</Label>
                  <Input id="rp-confirm" type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={busy} data-testid="reset-submit-btn"
                  className="w-full bg-[#0047AB] hover:bg-[#003380] text-white">
                  {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {invite ? "Set password & continue" : "Reset password"}
                </Button>
              </form>
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0047AB] mt-5">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
